import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, FileCheck, Users, Award, Clock, TrendingUp, CheckCircle } from 'lucide-react';
import SEO from '@/components/SEO';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import PricingTable from '@/components/PricingTable';
import TestimonialSlider from '@/components/TestimonialSlider';
import ProcessSlider from '@/components/ProcessSlider';
import TestimonialFAQSection from '@/components/TestimonialFAQSection';
import ContactPopup from '@/components/ContactPopup';
import MobileSlider from '@/components/MobileSlider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useGeolocation } from '@/hooks/useGeolocation';
import { submitLead, handleLeadResponse, getDialCodeFromCountry } from '@/utils/apiService';

const Home = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { geoData } = useGeolocation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    country_code: '+1',
    phone: '',
    message: ''
  });

  useEffect(() => {
    if (geoData?.data?.countryCode) {
      const dialCode = getDialCodeFromCountry(geoData.data.countryCode);
      if (dialCode) {
        setFormData(prev => ({ ...prev, country_code: dialCode }));
      }
    }
  }, [geoData]);

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    const numbersOnly = value.replace(/\D/g, '');
    const limitedValue = numbersOnly.slice(0, 10);
    setFormData(prev => ({ ...prev, phone: limitedValue }));
    if (limitedValue.length === 10 || limitedValue.length === 0) {
      setPhoneError('');
    } else {
      setPhoneError('Phone number must be 10 digits');
    }
  };

  const handleHeroSubmit = async (e) => {
    e.preventDefault();
    if (formData.phone.length !== 10) {
      setPhoneError('Phone number must be exactly 10 digits');
      return;
    }
    setIsSubmitting(true);
    setSubmitError('');
    try {
      const response = await submitLead(formData, geoData);
      handleLeadResponse(response, setSubmitError);
    } catch (error) {
      setSubmitError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const features = [
    {
      icon: Shield,
      title: 'Fortress-Grade Protection',
      description: 'Multi-layered IP protection strategy encompassing trademarks, copyrights, patents, and trade secrets—securing every facet of your competitive advantage with military-grade legal precision.'
    },
    {
      icon: FileCheck,
      title: 'AI-Enhanced Filing Excellence',
      description: 'Proprietary technology platform combines machine learning with attorney expertise, achieving the highest approval rate in the industry while significantly reducing filing time.'
    },
    {
      icon: Users,
      title: 'White-Glove Concierge Service',
      description: 'Dedicated senior attorney and paralegal team assigned exclusively to your account, providing round-the-clock access, proactive case management, and strategic counsel at every decision point.'
    },
    {
      icon: Award,
      title: 'Unmatched Track Record',
      description: 'Successfully prosecuted thousands of applications with zero malpractice claims in 18 years. Our results speak louder than promises.'
    },
    {
      icon: Clock,
      title: 'Accelerated Processing',
      description: 'Proprietary expedited filing protocols significantly reduce standard processing time. Real-time status dashboard provides complete transparency with automated milestone notifications.'
    },
    {
      icon: TrendingUp,
      title: 'Strategic Growth Advisory',
      description: 'Beyond registration—receive ongoing strategic counsel on IP portfolio expansion, licensing opportunities, enforcement strategies, and market positioning to maximize asset valuation.'
    }
  ];

  const services = [
    {
      title: 'Trademark Services',
      description: 'Complete trademark protection — registration, clearance search, ownership transfers, office actions, monitoring, and TTAB proceedings.',
      icon: Shield,
      link: '/services/trademark-registration',
      items: ['Register Your Trademark', 'Clearance Search', 'Ownership Transfers', 'Office Actions', 'Trademark Monitoring', 'TTAB Petition']
    },
    {
      title: 'Company Formation Services',
      description: 'Sophisticated business structuring including LLC, C-Corp, S-Corp, and Non-Profit formations with integrated tax optimization and liability shielding.',
      icon: FileCheck,
      link: '/services/llc',
      items: ['LLC Formation', 'C-Corporation', 'S-Corporation', 'Non-Profit Formation']
    },
    {
      title: 'Copyright Services',
      description: 'Full-spectrum copyright registration, DMCA compliance, enforcement, licensing, portfolio management, and infringement litigation.',
      icon: Award,
      link: '/services/register-copyright',
      items: ['Register Copyright', 'Copyright Enforcement', 'Copyright Transfers', 'Copyright Legal', 'Portfolio Management', 'DMCA Compliance']
    },
    {
      title: 'Patent Services',
      description: 'End-to-end patent services from prior art searches and patentability analysis to application filing, office actions, and legal protection.',
      icon: TrendingUp,
      link: '/services/patent-application',
      items: ['Patent Search', 'Patent Application', 'Patent Office Actions', 'Patent Legal Protection']
    },
    {
      title: 'Legal Services',
      description: 'Enterprise-grade legal counsel covering immigration law, due diligence, commercial transactions, data privacy compliance, and partnership structuring.',
      icon: Users,
      link: '/services/immigration-law',
      items: ['Immigration Law', 'Due Diligence', 'IT & Commercial Law', 'Partnership & LLP', 'Data Protection']
    },
    {
      title: 'Additional Corporate Services',
      description: 'Comprehensive corporate support including bookkeeping, tax advisory, CFO services, company renewals, name changes, and Amazon Brand Registry.',
      icon: Clock,
      link: '/services/bookkeeping',
      items: ['Bookkeeping', 'Tax Advisory', 'CFO Services', 'Company Renewal', 'Amazon Brand Registry', 'Good Standing']
    }
  ];

  return (
    <>
      <SEO 
        title="Initiate Trademark - Premier Intellectual Property Law Firm"
        description="Elite IP protection with industry-leading approval rate. Board-certified attorneys, AI-enhanced filing, faster processing. Trademark, copyright, patent & business formation services."
        keywords="intellectual property lawyer, trademark attorney, patent lawyer, copyright registration, business formation attorney, IP protection services, trademark registration, USPTO filing, brand protection"
      />
      <Header />
      
      {/* Hero Section - Enhanced Professional Design */}
      <section className="relative py-12 md:py-16 overflow-hidden" data-testid="hero-section">
        {/* Background Image */}
        <img
          src="/assets/img/bg-banner.webp"
          alt="Hero Background"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-primary-600/80"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            {/* Left Side - Content */}
            <div className="space-y-6 text-white order-last lg:order-first">
              <div className="inline-block">
                <span className="bg-secondary-600 text-white px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide">
                  Initiate Trademark
                </span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                <span className="block">Register Your Trademark</span>
                <span className="block mt-2 bg-white bg-clip-text text-white">
                  Protect What's Yours
                </span>
              </h1>
              
              <p className="text-lg text-gray-300 leading-relaxed max-w-xl">
                From filing to final approval — our trademark attorneys handle everything. Fast, affordable, and legally airtight protection for your Initiate Trademark, logo, and slogan.
              </p>

              {/* Trust Badges - Compact */}
              <div className="flex flex-wrap gap-2 pt-2">
                <div className="flex items-center space-x-1.5 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full border border-white/20">
                  <Shield className="w-4 h-4 text-secondary-500" />
                  <span className="text-xs">USPTO Registered</span>
                </div>
                <div className="flex items-center space-x-1.5 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full border border-white/20">
                  <Award className="w-4 h-4 text-secondary-500" />
                  <span className="text-xs">A+ Rated</span>
                </div>
                <div className="flex items-center space-x-1.5 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full border border-white/20">
                  <Users className="w-4 h-4 text-secondary-500" />
                  <span className="text-xs">Trusted Nationwide</span>
                </div>
              </div>
            </div>

            {/* Right Side - Compact Contact Form */}
            <div className="bg-white rounded-2xl p-6 shadow-2xl border border-gray-100 order-first lg:order-last" data-testid="hero-form">
              <div className="mb-5">
                <div className="inline-block mb-2">
                  <span className="bg-secondary-600 text-white px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide">
                    Free Trademark Consultation
                  </span>
                </div>
                <h2 className="text-2xl font-bold mb-1 text-gray-900 leading-tight">
                  Start Your Trademark Filing Today
                </h2>
                <p className="text-sm text-gray-600">
                  <strong className="text-secondary-600">No hidden fees</strong> · Expert-guided process · Results you can count on
                </p>
              </div>

              <form onSubmit={handleHeroSubmit} className="space-y-3">
                {submitError && (
                  <div className="bg-primary-50 border border-primary-200 text-primary-600 px-4 py-3 rounded-lg text-sm" data-testid="hero-submit-error">
                    {submitError}
                  </div>
                )}

                <div>
                  <Label htmlFor="hero-name" className="block text-xs font-semibold text-gray-700 mb-1">
                    Full Name *
                  </Label>
                  <Input
                    id="hero-name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="John Doe"
                    data-testid="hero-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="hero-email" className="block text-xs font-semibold text-gray-700 mb-1">
                    Email Address *
                  </Label>
                  <Input
                    id="hero-email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="john@example.com"
                    data-testid="hero-email-input"
                  />
                </div>

                <div>
                  <Label htmlFor="hero-phone" className="block text-xs font-semibold text-gray-700 mb-1">
                    Phone Number *
                  </Label>
                  <div className="flex gap-2">
                    <Select
                      value={formData.country_code}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, country_code: value }))}
                    >
                      <SelectTrigger className="w-24" data-testid="hero-country-code">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="+1">+1 (US)</SelectItem>
                        <SelectItem value="+44">+44 (UK)</SelectItem>
                        <SelectItem value="+91">+91 (IN)</SelectItem>
                        <SelectItem value="+61">+61 (AU)</SelectItem>
                        <SelectItem value="+49">+49 (DE)</SelectItem>
                        <SelectItem value="+33">+33 (FR)</SelectItem>
                        <SelectItem value="+81">+81 (JP)</SelectItem>
                        <SelectItem value="+86">+86 (CN)</SelectItem>
                        <SelectItem value="+82">+82 (KR)</SelectItem>
                        <SelectItem value="+55">+55 (BR)</SelectItem>
                        <SelectItem value="+52">+52 (MX)</SelectItem>
                        <SelectItem value="+39">+39 (IT)</SelectItem>
                        <SelectItem value="+34">+34 (ES)</SelectItem>
                        <SelectItem value="+31">+31 (NL)</SelectItem>
                        <SelectItem value="+46">+46 (SE)</SelectItem>
                        <SelectItem value="+41">+41 (CH)</SelectItem>
                        <SelectItem value="+65">+65 (SG)</SelectItem>
                        <SelectItem value="+971">+971 (AE)</SelectItem>
                        <SelectItem value="+27">+27 (ZA)</SelectItem>
                        <SelectItem value="+64">+64 (NZ)</SelectItem>
                        <SelectItem value="+92">+92 (PK)</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="flex-1">
                      <Input
                        id="hero-phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={handlePhoneChange}
                        placeholder="1234567890"
                        maxLength={10}
                        className={phoneError ? 'border-primary-500' : ''}
                        data-testid="hero-phone-input"
                      />
                      {phoneError && (
                        <p className="text-primary-500 text-xs mt-1">{phoneError}</p>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="hero-message" className="block text-xs font-semibold text-gray-700 mb-1">
                    How can we help? *
                  </Label>
                  <Textarea
                    id="hero-message"
                    required
                    rows={2}
                    value={formData.message}
                    onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                    placeholder="Tell us about your trademark needs..."
                    className="resize-none"
                    data-testid="hero-message-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting || !!phoneError}
                  className="w-full bg-secondary-600 text-white py-3 rounded-lg text-sm font-semibold hover:bg-primary-600 hover:text-white hover:shadow-lg transition-all disabled:opacity-50"
                  data-testid="hero-submit-btn"
                >
                  {isSubmitting ? 'Submitting...' : 'Register Your Trademark'}
                  {!isSubmitting && <ArrowRight className="ml-2 w-4 h-4" />}
                </Button>
              </form>

              <p className="text-xs text-gray-500 text-center mt-3">
                🔒 Your information is secure and confidential
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Informative Section - Consistent Alignment */}
      <section className="py-20 bg-white" data-testid="informative-section">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="space-y-6 order-last md:order-1 flex flex-col justify-center">
              <h2 className="text-4xl md:text-5xl font-bold leading-tight">
                Every Day Without Protection Puts Your Business at Risk
              </h2>
              <p className="text-xl text-gray-700 leading-relaxed">
                Your intellectual property represents years of investment, innovation, and reputation. Without proper legal protection, competitors can legally appropriate your brand identity, creative works, and proprietary innovations—costing you millions in lost revenue and market position.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Our board-certified intellectual property attorneys leverage cutting-edge technology and decades of collective experience to secure comprehensive protection for your assets. We combine the precision of AI-powered trademark searches with white-glove personal service, delivering industry-leading approval rates while maintaining the most competitive fee structures in the market.
              </p>
              <div className="flex gap-4 pt-4">
                <Button
                  onClick={() => setIsPopupOpen(true)}
                  className="bg-secondary-600 text-white px-8 py-3 rounded-full font-semibold hover:bg-secondary-600 hover:text-white hover:shadow-lg transition-all"
                >
                  Get Started
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </div>
            <div className="order-first md:order-2 relative min-h-[250px] md:min-h-[400px] rounded-3xl shadow-2xl overflow-hidden">
              <img
                src="/assets/img/about.webp"
                alt="Modern office building"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us - Consistent Alignment */}
      <section className="py-20 bg-transparent" data-testid="why-choose-us-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">The Initiate Trademark Advantage</h2>
            <p className="text-xl text-gray-600">Uncompromising excellence in intellectual property law—where speed, precision, and results converge</p>
          </div>

          <MobileSlider>
            {features.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <div
                  key={idx}
                  className="bg-white p-8 rounded-2xl card-hover border border-gray-100 h-full"
                  data-testid={`feature-${idx}`}
                >
                  <div className="w-16 h-16 bg-secondary-100 rounded-2xl flex items-center justify-center mb-6">
                    <Icon className="w-8 h-8 text-secondary-600" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </MobileSlider>
        </div>
      </section>

      {/* Services Overview - 3 Column Grid Without Images */}
      <section className="py-20 bg-gray-100 relative overflow-hidden" data-testid="services-section">
        {/* Abstract Background Pattern */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(124,58,237,0.05)_25%,rgba(124,58,237,0.05)_50%,transparent_50%,transparent_75%,rgba(139,92,246,0.05)_75%)] bg-[size:60px_60px]"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_30%,rgba(124,58,237,0.08),transparent_40%),radial-gradient(circle_at_80%_70%,rgba(139,92,246,0.08),transparent_40%)]"></div>
        </div>
        
        {/* Decorative Abstract Elements */}
        <div className="absolute top-10 left-10 w-72 h-72 bg-gray-200/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-gray-200/30 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-gray-200/20 rounded-full blur-2xl -translate-x-1/2 -translate-y-1/2"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="bg-secondary-600 text-white px-6 py-2 rounded-full text-sm font-semibold">
                What We Offer
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">Our Services</h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Comprehensive intellectual property and business solutions tailored to protect and grow your brand
            </p>
          </div>

          <MobileSlider>
            {services.map((service, idx) => {
              const Icon = service.icon;
              return (
                <div
                  key={idx}
                  className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-primary-200 flex flex-col h-full"
                  data-testid={`service-${idx}`}
                >
                  {/* Icon */}
                  <div className="mb-5">
                    <div className="w-14 h-14 bg-secondary-100 rounded-2xl flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300">
                      <Icon className="w-7 h-7 text-secondary-600" />
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-bold mb-3 text-gray-900">{service.title}</h3>
                  
                  {/* Description */}
                  <p className="text-gray-600 leading-relaxed mb-5 flex-grow">{service.description}</p>
                  
                  {/* CTA */}
                  <Link
                    to={service.link}
                    className="flex items-center font-semibold text-sm mt-auto transition-colors no-underline"
                    style={{ color: '#ff4d1b' }}
                  >
                    View Services
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" style={{ color: '#ff4d1b' }} />
                  </Link>

                  {/* Decorative Corner Element */}
                  <div className="absolute top-0 right-0 w-20 h-20 bg-primary-600/5 rounded-bl-full"></div>
                </div>
              );
            })}
          </MobileSlider>
        </div>
      </section>

      {/* Process Slider */}
      <ProcessSlider />

      {/* Additional Content Section - Image Left, Content Right */}
      <section className="py-20 bg-white" data-testid="additional-content-section">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Left Side - Image */}
            <div className="order-first md:order-1 relative min-h-[250px] md:min-h-[400px] rounded-3xl shadow-2xl overflow-hidden">
              <img
                src="/assets/img/service-home.webp"
                alt="Legal professionals discussing strategy"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
            
            {/* Right Side - Content */}
            <div className="space-y-6 order-last md:order-2 flex flex-col justify-center">
              <h2 className="text-4xl md:text-5xl font-bold leading-tight text-gray-900">
                Precision. Speed. Results. No Compromises.
              </h2>
              
              <p className="text-xl text-gray-700 leading-relaxed">
                For nearly two decades, Initiate Trademark has stood as the preeminent authority in intellectual property law, safeguarding client assets across all 50 states and internationally. Our reputation isn't built on marketing — it's forged through relentless excellence and measurable outcomes.
              </p>
              
              <p className="text-lg text-gray-600 leading-relaxed">
                We reject the assembly-line approach endemic to corporate law firms. Every engagement receives the full attention of our board-certified IP specialists, combining forensic-level due diligence with aggressive prosecution strategies. Our proprietary technology platform eliminates inefficiencies while our attorneys eliminate uncertainty — delivering ironclad protection in record time.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-secondary-100 rounded-xl flex items-center justify-center">
                      <Shield className="w-6 h-6 text-secondary-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Unrivaled Success Rate</h3>
                    <p className="text-sm text-gray-600">Industry-leading approval rate for all applications</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-secondary-100 rounded-xl flex items-center justify-center">
                      <Award className="w-6 h-6 text-secondary-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Elite Legal Team</h3>
                    <p className="text-sm text-gray-600">Board-certified IP specialists with extensive combined experience</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-secondary-100 rounded-xl flex items-center justify-center">
                      <Clock className="w-6 h-6 text-secondary-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Accelerated Timeline</h3>
                    <p className="text-sm text-gray-600">40% faster processing with real-time tracking</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-secondary-100 rounded-xl flex items-center justify-center">
                      <Users className="w-6 h-6 text-secondary-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Around-the-Clock Access</h3>
                    <p className="text-sm text-gray-600">Direct attorney access anytime—no intermediaries</p>
                  </div>
                </div>
              </div>

              <div className="pt-4">
                <Button
                  onClick={() => setIsPopupOpen(true)}
                  className="bg-secondary-600 text-white px-8 py-3 rounded-full font-semibold hover:bg-secondary-600 hover:text-white hover:shadow-lg transition-all"
                >
                  Get Started Today
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Table */}
      <PricingTable />

      {/* Testimonials & FAQs */}
      <TestimonialFAQSection />

      {/* CTA Section - Strong Call to Action with Background */}
      <section className="relative py-24 overflow-hidden" data-testid="cta-section">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="/assets/img/bg-banner.webp" 
            alt="Professional business team"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-primary-600/95"></div>
          <div className="absolute inset-0 bg-gray-200/20"></div>
        </div>

        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="inline-block mb-6">
              <span className="bg-secondary-600 text-white px-6 py-2 rounded-full text-sm font-semibold uppercase tracking-wide">
                Limited Time Offer
              </span>
            </div>
            
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Your Competitors Are Filing Right Now—Are You Willing to Risk Everything?
            </h2>
            
            <p className="text-xl md:text-2xl text-gray-200 mb-4 leading-relaxed">
              Every day without protection puts your brand at risk. The cost of delayed protection far exceeds the investment in securing your intellectual property today.
            </p>
            
            <p className="text-lg text-gray-300 mb-10 max-w-3xl mx-auto">
              Thousands of businesses have entrusted us with their most valuable assets. Our fortress-grade protection, combined with industry-leading speed and competitive pricing, ensures your intellectual property is secured before competitors can stake their claim.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Button
                onClick={() => setIsPopupOpen(true)}
                className="bg-secondary text-white px-10 py-6 rounded-full text-lg font-bold hover:bg-primary-600 hover:text-white hover:shadow-2xl hover:scale-105 transition-all"
                data-testid="cta-get-started-btn"
              >
                Secure Protection Now
                <ArrowRight className="ml-2 w-5 h-5" style={{ color: '#ffffff' }} />
              </Button>
              <Button
                onClick={() => setIsPopupOpen(true)}
                variant="outline"
                className="border-2 border-white text-white px-10 py-6 rounded-full text-lg font-bold hover:bg-white hover:text-primary-600 transition-all"
                data-testid="cta-consultation-btn"
              >
                Schedule Case Evaluation
              </Button>
            </div>

            <div className="flex flex-wrap justify-center gap-8 text-white/90">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5" style={{ color: '#ff4d1b' }} />
                <span className="text-sm">High Success Rate</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5" style={{ color: '#ff4d1b' }} />
                <span className="text-sm">Fast Processing</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5" style={{ color: '#ff4d1b' }} />
                <span className="text-sm">Thousands of Happy Clients</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <ContactPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
    </>
  );
};

export default Home;